import React, { useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import LoadingOverlay from './LoadingOverlay';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { fetchRevenueTrends, selectRevenueData } from '../store/slices/analyticsSlice';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const RevenueChart = () => {
    const dispatch = useAppDispatch();
    const { data: revenueData, loading, error } = useAppSelector(selectRevenueData);
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;

    useEffect(() => {
        if (restaurantId) {
            dispatch(fetchRevenueTrends(restaurantId));
        }
    }, [restaurantId, dispatch]);

    const chartData = useMemo(() => {
        if (!revenueData || !Array.isArray(revenueData)) {
            return {
                labels: [],
                datasets: [],
            };
        }

        // Calculate today's date and the date 6 days ago to get a 7-day window
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 6);

        // Filter records that fall between sevenDaysAgo and today
        const filteredData = revenueData.filter(item => {
            // Assume item._id is a date string in "YYYY-MM-DD" format.
            const itemDate = new Date(item._id);
            return itemDate >= sevenDaysAgo && itemDate <= today;
        });

        // Sort filtered data by date (ascending)
        filteredData.sort((a, b) => new Date(a._id) - new Date(b._id));

        const labels = filteredData.map(item =>
            new Date(item._id).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
            })
        );
        const revenues = filteredData.map(item => item.totalRevenue);

        return {
            labels,
            datasets: [
                {
                    label: 'Daily revenue (₹)',
                    data: revenues,
                    borderColor: 'rgba(249, 115, 22, 1)',
                    backgroundColor: 'rgba(249, 115, 22, 0.1)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.35,
                    pointRadius: 4,
                    pointBackgroundColor: 'rgba(249, 115, 22, 1)',
                },
            ],
        };
    }, [revenueData]);

    const handleRefresh = () => {
        if (restaurantId) {
            dispatch(fetchRevenueTrends(restaurantId));
        }
    };

    return (
        <>
            {loading && <LoadingOverlay />}
            <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Revenue (last 7 days)</p>
                        {error && (
                            <p className="text-[10px] text-red-500 mt-0.5">Failed to load data</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={loading}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                    >
                        <span className="text-xs">↻</span>
                        <span>{loading ? 'Loading' : 'Refresh'}</span>
                    </button>
                </div>
                <div className="mt-1" style={{ height: 220 }}>
                    <Line
                        data={chartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { position: 'bottom', labels: { boxWidth: 8, font: { size: 10 } } },
                                title: { display: false },
                                tooltip: {
                                    callbacks: {
                                        label: (context) => {
                                            const value = context.parsed.y ?? 0;
                                            return ` ₹${Number(value).toLocaleString('en-IN')}`;
                                        },
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    ticks: { font: { size: 9 } },
                                    grid: { display: false },
                                },
                                y: {
                                    ticks: {
                                        font: { size: 9 },
                                        callback: (value) => `₹${Number(value).toLocaleString('en-IN')}`,
                                    },
                                    grid: {
                                        color: 'rgba(148, 163, 184, 0.2)',
                                        drawBorder: false,
                                        borderDash: [4, 4],
                                    },
                                },
                            },
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default RevenueChart;
